import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Bold, ChevronDown, ChevronUp } from "lucide-react";
import useRequireAuth from "../../../../utils/useRequireAuth";
import { BASE_URL, CUSTOMER_NOTES_ADD_UPDATE } from "../../../../utils/constants";
import { Avatar } from "@mui/material";
import {
  filterNotesByTags,
  getFormData,
  getHexWithOpacity,
  getInitials,
  getUniqueTags,
  TagOptionsColored,
} from "../../../../utils/utilityFunctions/stringUtility";
import { getFormatDate } from "../../../../utils/utilityFunctions/dateUtility";
import { saveNotes } from "../../../../utils/customer/customerNotesSlice";

import ToolTipComponent from "../../../UniversalComponents/ToolTipComponent";
import { ReactComponent as PinIcon } from "../../../../assets/images/icons/TitlePin.svg";
import { ReactComponent as LatestPinUnpin } from "../../../../assets/images/icons/LatestPinUnpin.svg";
import { ReactComponent as NoNotesIcon } from "../../../../assets/images/NoNotesIcon.svg";
import { ReactComponent as Attachment } from "../../../../assets/images/icons/Attachment.svg";
import { ReactComponent as PreviewIcon } from "../../../../assets/images/icons/Preview.svg";
import { ReactComponent as NotesView } from "../../../../assets/images/icons/NotesView.svg";
import { motion } from "framer-motion";
import AvatarName from "../../../UniversalComponents/AvatarName";
import { usePostApiMutation } from "../../../../utils/customer/customerTaskTable";
import { pushAlert } from "../../../../utils/alertSlice";
import { hideLoader, showLoader } from "../../../../utils/loaderSlice";
import CustomDrawer from "../../../UniversalComponents/CustomDrawer";
import MotionSearchBox from "../../../UniversalComponents/MotionSearchBox";
import AddButton from "../../../UniversalComponents/AddButton";
import { Close } from "@mui/icons-material";
import LoaderWrapper from "../../../UniversalComponents/CustomLoader";
import { ReactComponent as NoNotesFound } from "../../../../assets/images/icons/NoNotesFound.svg";
import { getUserDateTime } from "../../../../utils/utilityFunctions/UTCFormat";

const highlightTextInHtml = (html, highlight) => {
  if (!highlight.trim()) return html;

  const regex = new RegExp(`(${highlight})`, "gi");
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    const text = node.textContent;
    if (regex.test(text)) {
      const span = document.createElement("span");
      span.innerHTML = text.replace(regex, (match) => `<span class="bg-yellow-300">${match}</span>`);
      node.replaceWith(span);
    }
  }

  return tempDiv.innerHTML;
};

const PararagraphComponent = ({ CategoryData, searchTerm }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const paragraphRef = useRef(null);

  useEffect(() => {
    const checkHeight = () => {
      if (paragraphRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(paragraphRef.current).lineHeight);
        const height = paragraphRef.current.scrollHeight;

        const maxHeight = lineHeight * 3;

        setShowReadMore(height > maxHeight);
      }
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(checkHeight);
    if (paragraphRef.current) {
      resizeObserver.observe(paragraphRef.current);
    }

    return () => {
      if (paragraphRef.current) {
        resizeObserver.unobserve(paragraphRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [CategoryData]);

  const handleReadMoreClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className=" w-full overflow-auto">
      <p
        ref={paragraphRef}
        className={`text-[11px] font-normal w-full max-w-[20vw] text-justify text-[#666] break-words ${isExpanded ? "" : "line-clamp-3 "}`}
        dangerouslySetInnerHTML={{
          __html: highlightTextInHtml(CategoryData, searchTerm),
        }}
      ></p>

      {showReadMore && (
        <button
          className="text-blue-500 hover:text-blue-600 font-300 text-xs cursor-pointer"
          onClick={(e) => handleReadMoreClick(e)}
        >
          {isExpanded ? "Read less" : "Read more"}
          {/* View Full Note */}
        </button>
      )}
    </div>
  );
};
const PararagraphComponentForPinnedNote = ({ CategoryData, searchTerm }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const paragraphRef = useRef(null);

  useEffect(() => {
    const checkHeight = () => {
      if (paragraphRef.current) {
        const lineHeight = parseInt(window.getComputedStyle(paragraphRef.current).lineHeight);
        const height = paragraphRef.current.scrollHeight;

        const maxHeight = lineHeight * 3;

        setShowReadMore(height > maxHeight);
      }
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(checkHeight);
    if (paragraphRef.current) {
      resizeObserver.observe(paragraphRef.current);
    }

    return () => {
      if (paragraphRef.current) {
        resizeObserver.unobserve(paragraphRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [CategoryData]);

  const handleReadMoreClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="w-full overflow-auto">
      <p
        ref={paragraphRef}
        className={`text-[11px] font-normal w-full  text-justify text-[#666] break-words ${isExpanded ? "" : "line-clamp-1 "}`}
        dangerouslySetInnerHTML={{
          __html: highlightTextInHtml(CategoryData, searchTerm),
        }}
      ></p>

      {showReadMore && (
        <button
          className="text-blue-500 hover:text-blue-600 font-300 text-xs cursor-pointer"
          onClick={(e) => handleReadMoreClick(e)}
        >
          {isExpanded ? "Read less" : "Read more"}
          {/* View Full Note */}
        </button>
      )}
    </div>
  );
};

const NotesContent = ({
  notesData,
  refetchNotesData,
  searchTerm,
  handleEditNote,
  setIsPinnedDrawerOpen,
  noteMain,
  isSearchActive,
  setIsSearchActive,
  searchText,
  setSearchText,
  isActive,
  HandlePreviewData,
  OpenAddEditDrawer,
  sectionType = "Tracking",
  rawNotesData,
  isNotesFetching = false,
  isCustomModule = false,
  Item,
}) => {
  const [loading, setLoading] = useState(false);
  const { token } = useRequireAuth();
  const dispatch = useDispatch();
  // const [contentCount, setContentCount] = useState(10);
  //const [searchTerm, setSearchTerm] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const containerRef = useRef(null);
  const companyLocalData = localStorage.getItem("company_data");
  const parsedCompanyData = JSON.parse(companyLocalData);
  const [color, setColor] = useState(null);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [pinnedCount, setPinnedCount] = useState(0);
  const [isPinnedVisible, setIsPinnedVisible] = useState(false);
  const [isTagVisible, setIsTagVisible] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [
    AddUpdateNotes,
    {
      data: NotesAddUpdateData,
      isError: NotesAddUpdateError,
      isSuccess: NotesAddUpdateSuccess,
      isLoading: NotesAddUpdateLoading,
    },
  ] = usePostApiMutation();
  useEffect(() => {
    //loading
    if (NotesAddUpdateLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
    //try block
    if (NotesAddUpdateSuccess && NotesAddUpdateData.success) {
      refetchNotesData();
    }

    // catch block
    if (NotesAddUpdateError) {
      dispatch(pushAlert({ message: "something went wrong", type: "danger", visible: true }));
    }
  }, [NotesAddUpdateLoading, NotesAddUpdateSuccess, NotesAddUpdateError]);

  const toggleExpand = (id) => {
    setExpandedNotes((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  const getNotesMatchingTags = (notesData, selectedTags) => {
    if (!selectedTags?.length) return notesData;

    const selectedValues = new Set(selectedTags.map((tag) => tag.label));

    return notesData?.filter((note) => {
      if (!Array.isArray(note.tags)) return false;

      return note.tags.some((tag) => selectedValues.has(tag.label));
    });
  };

  const filteredNotes = getNotesMatchingTags(notesData, selectedTags)?.filter(
    (note) =>
      note?.notes_title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      note?.notes_body?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      note?.category_name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // useEffect(() => {
  const pinnedNotesData = filteredNotes.filter((note) => note.is_pinned === true);
  //   setPinnedNotes(pinnedNotesData);
  //   setPinnedCount(pinnedNotesData.length);
  // }, [filteredNotes]);

  useEffect(() => {
    if (pinnedNotesData?.length < 1) {
      if (isPinnedVisible) {
        setIsPinnedVisible(false);
        setIsPinnedDrawerOpen(false);
      }
    }
  }, [pinnedNotesData?.length]);

  const isContentLong = (content) => {
    const testElement = document.createElement("div");
    testElement.style.position = "absolute";
    testElement.style.visibility = "hidden";
    testElement.style.height = "auto";
    testElement.style.width = "300px";
    testElement.style.lineHeight = "20px";
    testElement.innerText = content;
    document.body.appendChild(testElement);
    const isLong = testElement.clientHeight > 70;
    document.body.removeChild(testElement);
    return isLong;
  };

  const handlePinned = async (note) => {
    const notesData = {
      notes_category_id: note.notes_category_id,
      colour_code: note.colour_code,
      notes_title: note.notes_title,
      company_id: parsedCompanyData.company_id,
      notes_body: note.notes_body,
      rag_status: "wip",
      id: note.id,
      is_pinned: !Boolean(note.is_pinned),
    };
    const PinUnpinNote = getFormData(notesData);

    try {
      const response = await AddUpdateNotes({
        data: notesData,
        url: `${CUSTOMER_NOTES_ADD_UPDATE}?${isCustomModule ? `template_id=${Item?.template_id}&row_id=${Item?.row_id}` : `type=${sectionType}`}`,
      });
      if (response?.success || response?.data?.success || response?.data?.status) {
        dispatch(
          pushAlert({
            message: !Boolean(note.is_pinned) ? "Note pinned successfully." : "Note unpinned successfully.",
            type: "success",
            visible: true,
          })
        );
      }
    } catch (error) {
      dispatch(hideLoader());
      dispatch(
        pushAlert({
          message: error.message || "Error updating pin status",
          type: "danger",
          visible: true,
        })
      );
    }
  };

  const handleClickScroll = (note) => {
    const noteElement = document.getElementById(`sidenote-${note.id}`);

    if (noteElement) {
      noteElement.scrollIntoView({ behavior: "smooth", block: "center" });
      noteElement.classList.add("animate-zoom-in", "border-2", "border-[#50C878]", "p-2", "rounded-[5px]");
      if (isContentLong(note.body)) {
        toggleExpand(note.id);
      }

      setTimeout(() => {
        noteElement.classList.remove("border-2", "border-[#50C878]", "p-2", "rounded-[5px]");
        noteElement.classList.add("transition-all", "duration-600", "opacity-100");
      }, 2000);
    }
  };

  const handleCheckboxChange = (e, tag, isChecked) => {
    e.stopPropagation();
    if (isChecked) {
      setSelectedTags((prev) => [...prev, tag]);
    } else {
      setSelectedTags((prev) => prev.filter((t) => t !== tag));
    }
  };

  return (
    <>
      {Boolean(filteredNotes?.length) && (
        <div className="flex items-center justify-between w-full mt-2 px-3 gap-x-3">
          <AddButton
            onClick={OpenAddEditDrawer}
            type={"button"}
            isActive={isActive}
            // activeText = "Cancel"
          />

          <MotionSearchBox
            isSearchActive={isSearchActive}
            setIsSearchActive={setIsSearchActive}
            searchText={searchText}
            setSearchText={setSearchText}
            expanded
            placeholder={"Search For Notes"}
          />
        </div>
      )}

      {
        <div className="relative mt-3 px-3">
          {/* Toggle Button */}
          <div className="flex items-center justify-between w-full gap-2">
            {pinnedNotesData?.length > 0 && (
              <div
                onClick={() => {
                  setIsPinnedVisible(!isPinnedVisible);
                  setIsPinnedDrawerOpen(!isPinnedVisible);
                  setIsTagVisible(false);
                }}
                className={`flex justify-between items-center w-1/2 px-2 py-1 mb-1 bg-white text-[#535252] rounded-md cursor-pointer border border-[#1d44691a] rounded-md"
              `}
              >
                <span className="text-12 flex items-center font-500 text-[#383838]">
                  <PinIcon className="mr-2" /> Pinned Notes ({pinnedNotesData?.length})
                </span>

                <div
                  className={`flex items-center justify-center h-7 w-7 border rounded-lg border-[#D2DAE1] ${isPinnedVisible ? "bg-[#2999bc]" : "bg-[#F0F0F0]"}`}
                >
                  <NotesView fill={isPinnedVisible ? "white" : "#8E8E8E"} />
                </div>

                {/* <span>
                <motion.div
                  animate={{ rotate: isPinnedVisible ? 0 : 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  {isPinnedVisible ? (
                    <ChevronUp size={22} fontStyle={700} className="text-[#535252]" />
                  ) : (
                    <ChevronDown size={22} />
                  )}
                </motion.div>
              </span> */}
              </div>
            )}
            <div
              onClick={() => {
                setIsTagVisible(!isTagVisible);
                setIsPinnedDrawerOpen(!isTagVisible);
                setIsPinnedVisible(false);
              }}
              className={`flex justify-between items-center ${pinnedNotesData?.length > 0 ? "w-1/2" : "w-full"} gap-1 px-2 py-1 mb-1 bg-white text-[#535252] rounded-md cursor-pointer border border-[#1d44691a] rounded-md"
              `}
            >
              <span className="text-12 flex items-center font-500 text-[#383838]">Tags List</span>

              <div
                className={`flex items-center justify-center h-7 w-7 border rounded-lg border-[#D2DAE1] ${isTagVisible ? "bg-[#2999bc]" : "bg-[#F0F0F0]"}`}
              >
                <NotesView fill={isTagVisible ? "white" : "#8E8E8E"} />
              </div>

              {/* <span>
                <motion.div
                  animate={{ rotate: isPinnedVisible ? 0 : 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  {isPinnedVisible ? (
                    <ChevronUp size={22} fontStyle={700} className="text-[#535252]" />
                  ) : (
                    <ChevronDown size={22} />
                  )}
                </motion.div>
              </span> */}
            </div>
          </div>
        </div>
        // </div>
      }

      {/* drawer for Pinned Notes */}
      <CustomDrawer
        open={isPinnedVisible}
        anchorParentRef={noteMain}
        subDrawer
        onClose={() => {
          setIsPinnedVisible(false);
          setIsPinnedDrawerOpen(false);
        }}
        coloredStyle
        headerText={"Pinned Notes"}
        width={"25vw"}
      >
        <div className="h-full overflow-y-auto  notesscrollbar mx-2 overflow-x-hidden">
          {pinnedNotesData.map((note, idx) => (
            <div className="group">
              <div
                className={` hover:bg-[#dae4ff78] rounded-lg cursor-pointer my-2 `}
                // style={
                //   idx % 2 === 0
                //     ? { borderLeft: `4px solid ${getHexWithOpacity(note?.colour_code, 40)}` }
                //     : { borderRight: `4px solid ${getHexWithOpacity(note?.colour_code, 40)}` }
                // }
                key={note.id}
                onClick={() => {
                  // setIsPinnedVisible(false);
                  // setIsPinnedDrawerOpen(false)
                  handleClickScroll(note);
                }}
              >
                <div className="flex items-start justify-between">
                  {/* <div className="flex items-start mr-2 mt-1">
                            <AvatarName
                              name={getInitials(note.updated_by)}
                              size="medium"
                              bgColor={"#E8ECF299"}
                              textColor={"#7C7C83"}
                              borderColor={"#7C7C83"}
                            />
                          </div> */}

                  <div className="flex flex-col items-start justify-evenly grow min-w-0 px-3 py-1">
                    <div className="flex items-center justify-between pb-2 w-full gap-x-2">
                      {/* Note Details */}
                      <div className="flex flex-col items-start justify-center min-w-0 grow">
                        <div
                          className="text-[#1D4469] text-sm font-medium capitalize w-full truncate"
                          style={{ color: note?.colour_code }}
                          dangerouslySetInnerHTML={{
                            __html: highlightTextInHtml(note.notes_title, searchTerm),
                          }}
                        />
                        <div className="flex items-center justify-centre w-full">
                          <span className="text-[#767676] text-xs font-normal  capitalize pr-1">{note.created_by}</span>
                          <span className="mx-1 bg-[#D2DAE1] w-[1px] h-3"></span>
                          <span className="text-[#767676] text-xs font-normal capitalize">
                            {getFormatDate(note.created_at)}
                          </span>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-x-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <span onClick={() => handlePinned(note)} className="w-[15px] group">
                          <ToolTipComponent text={"Unpin"} placement="left">
                            <LatestPinUnpin
                              className={`cursor-pointer ${
                                Boolean(note?.is_pinned)
                                  ? "fill-[#00BA00] group-hover:fill-[#30a930]"
                                  : "fill-[#8E8E8E] group-hover:fill-black"
                              }`}
                            />
                          </ToolTipComponent>
                        </span>
                        <span className="hover:cursor-pointer" onClick={() => HandlePreviewData(note?.id)}>
                          <PreviewIcon className="fill-[#8E8E8E] hover:fill-black" />
                        </span>
                        {/* Category */}
                        {/* <div className="flex items-center justify-evenly border border-[#D2DAE1] rounded m-1 px-2 py-1">
                                  <div
                                    className=" text-sm font-400 mr-1"
                                    dangerouslySetInnerHTML={{
                                      __html: highlightTextInHtml(note?.category_name, searchTerm),
                                    }}
                                  ></div>
                                  <i
                                    className="bi bi-circle-fill text-[10px] "
                                    style={{ color: note.colour_code || "#000" }}
                                  ></i>
                                </div> */}
                      </div>
                    </div>

                    <PararagraphComponentForPinnedNote
                      CategoryData={note.notes_body}
                      searchTerm={searchTerm}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div className="w-full flex items-centre justify-end"></div>
                {/* Note Content */}
              </div>
              <div className="border-b border-dashed border-[#1d446952] mx-2 transition-opacity duration-300 group-hover:opacity-0"></div>
            </div>
          ))}
        </div>
      </CustomDrawer>

      {/* drawer for Tags   */}
      <CustomDrawer
        open={isTagVisible}
        anchorParentRef={noteMain}
        subDrawer
        onClose={() => {
          setIsTagVisible(false);
          setIsPinnedDrawerOpen(false);
        }}
        coloredStyle
        headerText={"Tags List"}
        width={"25vw"}
      >
        <div className="w-full  flex flex-col bg-[#F8FBFF] p-2 rounded-lg overflow-y-auto thinscrollbar min-h-0 grow">
          {/* Search Box */}
          {/* <div className="flex items-center justify-between gap-x-2 mb-2">
            <MotionSearchBox
              isSearchActive={isSearchOpen}
              setIsSearchActive={setIsSearchOpen}
              searchText={tagSearchTerm}
              setSearchText={setTagSearchTerm}
              placeholder={"Search For Notes..."}
              expanded
              ClassName={"h-[35px]"}
            />
          </div> */}

          {/* Scrollable Tag List */}
          <div className="flex-1 min-h-0 overflow-y-auto thinscrollbar">
            <div className="flex flex-col items-start gap-y-2 p-2">
              {getUniqueTags(rawNotesData).map((tag, index) => (
                <div key={index} className="flex items-center gap-x-2">
                  <input
                    type="checkbox"
                    id="tag-123"
                    className={`mr-2 focus:outline-none focus:ring-0 rounded-sm border-[#D9D9D9]`}
                    checked={selectedTags.includes(tag)}
                    onChange={(e) => handleCheckboxChange(e, tag, e.target.checked)}
                  />
                  <p
                    className="text-sm text-white max-w-[30vw] py-1 px-2 rounded-md font-normal"
                    style={{
                      backgroundColor: getHexWithOpacity(tag?.color_code, 10) || "#666",
                      color: tag?.color_code,
                      border: `1px solid ${tag?.color_code}`,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightTextInHtml(tag?.label, tagSearchTerm),
                    }}
                  ></p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CustomDrawer>

      {
        // notes Content Preview
        <div className="flex-1  overflow-x-hidden border-b  ">
          <LoaderWrapper isLoading={isNotesFetching}>
            <div className="flex-1 h-full overflow-y-auto   overflow-x-hidden  border-b pr-1 notesscrollbar">
              {notesData?.length > 0 ? (
                filteredNotes?.length > 0 ? (
                  filteredNotes?.map((note, idx) => (
                    <div className="group">
                      <div className="flex flex-col " key={note?.id} id={`sidenote-${note.id}`}>
                        <div
                          className={`hover:bg-[#dae4ff78] rounded-lg cursor-pointer m-2 px-3 `}
                          // style={
                          //   idx % 2 === 0
                          //     ? { borderLeft: `4px solid ${getHexWithOpacity(note?.colour_code, 40)}` }
                          //     : { borderRight: `4px solid ${getHexWithOpacity(note?.colour_code, 40)}` }
                          // }
                        >
                          <div className=" flex items-start justify-between p-1">
                            {/* <div className="mr-3 mt-1">
                        <AvatarName
                          name={getInitials(note.updated_by)}
                          size="medium"
                          bgColor={"#e8ecf299"}
                          textColor={"#7C7C83"}
                          borderColor={"#7C7C83"}
                        />
                      </div> */}

                            <div className="flex items-start flex-col grow min-w-0">
                              <div className="flex justify-between w-full">
                                <div className="flex items-start gap-x-1 w-full">
                                  <div className="min-w-0 grow truncate">
                                    <p
                                      className=" max-w-72 truncate text-[#383838] text-sm font-500 capitalize pb-[1px] "
                                      style={{ color: note?.colour_code }}
                                      dangerouslySetInnerHTML={{
                                        __html: highlightTextInHtml(note.notes_title, searchTerm),
                                      }}
                                    ></p>

                                    <div className="flex items-center justify-start gap-x-2">
                                      <div className="text-[#767676] text-xs font-normal  capitalize pr-1">
                                        {note.created_by}
                                      </div>
                                      <span className="h-3 w-[1px] bg-[#767676]"></span>

                                      <div className="text-[#767676] text-xs font-normal  capitalize pr-1">
                                        {getUserDateTime(note.created_at)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center  opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                  <span
                                    onClick={() => handlePinned(note)}
                                    className="flex items-center justify-center hover:cursor-pointer me-1 w-6 h-7 group"
                                  >
                                    <ToolTipComponent
                                      text={Boolean(note?.is_pinned) ? "Unpin" : "Pin"}
                                      placement="left"
                                    >
                                      <LatestPinUnpin
                                        className={`cursor-pointer ${
                                          Boolean(note?.is_pinned)
                                            ? "fill-[#00BA00] group-hover:fill-[#30a930]"
                                            : "fill-[#8E8E8E] hover:fill-black"
                                        }`}
                                      />
                                    </ToolTipComponent>
                                  </span>
                                  <span className="hover:cursor-pointer" onClick={() => HandlePreviewData(note?.id)}>
                                    <PreviewIcon className="fill-[#8E8E8E] hover:fill-black" />
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-start w-full noteViewPad">
                                <PararagraphComponent CategoryData={note.notes_body} searchTerm={searchTerm} />
                              </div>

                              <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mt-1">
                                {note?.tags?.map((option) => {
                                  return (
                                    <span
                                      key={option?.value}
                                      className="text-white text-xs font-medium px-2 py-1 rounded items-center"
                                      style={{
                                        backgroundColor: getHexWithOpacity(option?.color_code, 10) || "#666",
                                        color: option?.color_code,
                                        border: `1px solid ${option?.color_code}`,
                                      }}
                                    >
                                      {option?.label}
                                      {/* <button type="button" className="ml-2 text-white">
                                      <i className="bi bi-x"></i>
                                    </button> */}
                                    </span>
                                  );
                                })}
                              </div>

                              <div className="gap-2 w-full flex items-centre justify-end">
                                {note?.attachments?.length > 0 && (
                                  <div className="flex items-center justify-center w-7 h-7 border rounded-lg bg-[#F0F0F0] border-[#D2DAE1]">
                                    <Attachment />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="border-b-2"></div>
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-dashed border-[#1d446952] mx-3 transition-opacity duration-300 group-hover:opacity-0"></div>
                    </div>
                  ))
                ) : (
                  <div className="h-[calc(100vh-160px)]">
                    <div className="flex justify-center items-center flex-col space-y-2 h-full ">
                      <NoNotesFound />
                      {/* <span className="text-14 text-black text-sm font-400">No Notes is Available.</span> */}
                      <span
                        // onClick={handleEditNote}
                        // className="border px-5 py-1.5   cursor-pointer items-center text-center text-sm font-400  hover:border-[#1D4469] hover:text-[#1D4469] text-[#383838] rounded-3xl shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10)_inset,0px_0px_4px_0px_rgba(0,0,0,0.15)]"
                        className="text-[#666666] font-Inter font-400 text-14"
                      >
                        No record found.
                      </span>
                    </div>
                  </div>
                )
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <div className="flex justify-center items-center flex-col space-y-2 h-full ">
                    <NoNotesIcon />
                    <span className="text-14 text-black text-sm font-400">
                      {" "}
                      No notes available. Create a note to get started.
                    </span>
                    <span
                      onClick={handleEditNote}
                      className="border px-5 py-1.5   cursor-pointer items-center text-center text-sm font-400 bg-[#345fc4] hover:bg-[#072875] hover:border-[#1D4469] text-[#ffff] rounded-md shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10)_inset,0px_0px_4px_0px_rgba(0,0,0,0.15)]"
                    >
                      + Add Note
                    </span>
                  </div>
                </div>
              )}
            </div>
          </LoaderWrapper>
        </div>
      }
      {loading && (
        <div className="flex my-2 border-b border-[#203C501A]">
          <div className="mr-3">
            <div className="animate-pulse bg-gray-200 rounded-full h-[30px] w-[30px]"></div>
          </div>
          <div>
            <div className="animate-pulse bg-gray-200 h-[16px] w-[100px] mb-1"></div>
            <div className="animate-pulse bg-gray-200 h-[16px] w-[150px]"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesContent;
